/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useQuery } from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import capitalize from 'lodash/capitalize';

// Lib
import { apiClient } from '@lib/api-client';

// Types
import {
	TApiResponseGetBrandsPopulated,
	TParsedBrand,
	TApiResponseBrandPopulated,
} from '../types';
import {
	TApiParams,
	TParams,
} from '@global-types/app/app';

// Utils
import { formatDate } from '@utils/functions';
import { getCountryName } from '@utils/countries';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';
import { queryKeys } from '@config/constants/query-keys';
import { useAuthorization } from '@lib/authorization';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getBrands = (
	params: TApiParams
): Promise<TApiResponseGetBrandsPopulated> => {
	return apiClient.get('/brands', {
		params: {
			page: params.page,
			pageSize: params.pageSize,

			populate: [
				'organization',
				'contact',
				'verifiedBy',
				'logo',
			],
			query: params.queryParams,
			sort: params.sortParams,
		},
	});
};

export const getBrandsWithoutContact = (
	params: TApiParams
): Promise<TApiResponseGetBrandsPopulated> => {
	return apiClient.get('/brands', {
		params: {
			page: params.page,
			pageSize: params.pageSize,

			populate: ['organization', 'verifiedBy', 'logo'],
			query: params.queryParams,
			sort: params.sortParams,
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useBrandsApi = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { checkRole } = useAuthorization();
	const { data, isFetching, isFetched, isPending } =
		useQuery({
			queryKey: [
				queryKeys.app.brands.list.key,
				{
					page: page,
					size: size,
					queryParams: queryParams,
					sortParams: sortParams,
				},
			],
			queryFn: () => {
				if (
					checkRole([
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					])
				) {
					return getBrands({
						page: page,
						pageSize: size,
						queryParams: queryParams,
						sortParams: sortParams,
					});
				}
				return getBrandsWithoutContact({
					page: page,
					pageSize: size,
					queryParams: queryParams,
					sortParams: sortParams,
				});
			},

			staleTime: 0,
		});

	const updateList = (
		Brands: TApiResponseBrandPopulated[]
	) =>
		Brands.reduce(
			(
				accumulator: TParsedBrand[],
				currentItem: TApiResponseBrandPopulated
			) => {
				const modifiedItem: TParsedBrand = {
					...currentItem,
					countryCode: currentItem?.location?.countryCode
						? getCountryName(
								currentItem.location.countryCode
							)
						: NOT_APPLICABLE,
					place: currentItem?.location?.place
						? currentItem.location.place
						: NOT_APPLICABLE,
					organization: currentItem?.organization?.name
						? currentItem.organization.name
						: NOT_APPLICABLE,
					organizationId: currentItem?.organization?.id
						? currentItem.organization.id
						: NOT_APPLICABLE,
					createdAt: currentItem?.createdAt
						? formatDate(
								currentItem.createdAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					updatedAt: currentItem?.updatedAt
						? formatDate(
								currentItem.updatedAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					verifiedBy:
						!isNil(currentItem.verified) &&
						!isNil(currentItem.verifiedBy)
							? `${currentItem.verifiedBy.firstName} ${currentItem.verifiedBy.lastName}`
							: NOT_APPLICABLE,
					verifiedAt:
						!isNil(currentItem.verified) &&
						!isNil(currentItem.verifiedAt)
							? formatDate(
									currentItem.verifiedAt,
									'DD/MM/YYYY'
								)
							: NOT_APPLICABLE,
					legalIdentifier: currentItem?.legalIdentifier
						? currentItem.legalIdentifier
						: NOT_APPLICABLE,
					contactName: currentItem.contact?.firstName
						? `${capitalize(currentItem.contact.firstName)} ${currentItem.contact.lastName}`
						: NOT_APPLICABLE,
					contactEmail: currentItem?.contact?.email
						? `${currentItem.contact.email}`
						: NOT_APPLICABLE,
					contactPhone: currentItem?.contact?.phoneNumber
						? `${currentItem.contact.phoneNumber}`
						: NOT_APPLICABLE,
				};
				accumulator.push(modifiedItem);

				return accumulator;
			},
			[]
		);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		isPending,
		total: data?.total ?? 0,
		page: data?.page ?? 0,
		pageSize: data?.pageSize ?? 0,
		totalPages: data?.totalPages ?? 0,
	};
};
